/**
 * DeveloperWelcomePage - Welcome/Thank You Page after Stripe Payment
 *
 * Simple, clean layout with essential content:
 * - Centered card with max-w-2xl width
 * - White background for page, gray-50 for card
 * - Footer outside card
 * - Uses ECGridColors for styling
 * - Simple paragraph-based content for clarity
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { CheckCircle2 } from 'lucide-react';
import { AnonymousPortalLayout } from '@/components/common/AnonymousPortalLayout';
import { ECGridColors } from '@/types/colors';

// ============================================================================
// Props
// ============================================================================

export interface DeveloperWelcomePageProps {
  subdomain?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export const DeveloperWelcomePage = React.forwardRef<HTMLDivElement, DeveloperWelcomePageProps>(
  ({ subdomain }, ref) => {
    const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);

  return (
    <AnonymousPortalLayout ref={ref}>
      <div className="w-full max-w-2xl space-y-6">
          {/* Welcome Card */}
          <div className="bg-gray-50 rounded-lg shadow-md border border-gray-200 p-6 sm:p-8">
            {/* Success Header */}
            <div className="text-center mb-8">
              {/* Success Icon */}
              <div className="mb-4">
                <div
                  className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${ECGridColors.blue}15` }}
                >
                  <CheckCircle2 className="h-8 w-8" style={{ color: ECGridColors.blue }} />
                </div>
              </div>

              {/* ECGrid Logo */}
              <div className="mb-4">
                <img
                  src="/img/ecgridlogo2x.png"
                  alt="ECGrid"
                  className="h-12 mx-auto"
                />
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: ECGridColors.tealDark }}>
                {t('DeveloperWelcome_Title', 'Thank You for Signing Up')}
              </h1>

              {/* Subtitle */}
              <p className="text-lg sm:text-xl mb-8" style={{ color: ECGridColors.blue }}>
                {t('DeveloperWelcome_Subtitle', "You're all set!")}
              </p>
            </div>

            {/* Content Paragraphs */}
            <div className="space-y-6 text-gray-700">
              {/* Paragraph 1 - Introduction */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t(
                  'DeveloperWelcome_Paragraph1',
                  'Welcome to the ECGrid Developer Program. ECGrid provides the API-first connectivity infrastructure necessary to embed B2B communications directly into your platform. Our architecture delivers real-time visibility and self-service tools that enable you to scale your operations while maintaining a native experience for your users.'
                )}
              </p>

              {/* Paragraph 2 - Credentials Email */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t(
                  'DeveloperWelcome_Paragraph2',
                  'A secure message containing your login credentials has been sent to your email address. If this communication does not appear in your inbox, please verify your junk or spam folders. Should you require further assistance, our support team is available at support@ecgrid.com to ensure you have immediate access to your environment.'
                )}
              </p>

              {/* Paragraph 3 - Onboarding & Resources (merged with KB link) */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t(
                  'DeveloperWelcome_Paragraph3',
                  'Upon your initial login, you will be guided through an orientation of the ECGrid portal to familiarize your team with our core infrastructure tools. You also have immediate access to our technical documentation and the Resource Center located in the lower section of your portal. Detailed operational guidance is available through our Knowledge Base at '
                )}
                <a
                  href="https://support.ecgrid.com/support/home"
                  className="font-medium hover:underline"
                  style={{ color: ECGridColors.blue }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://support.ecgrid.com/support/home
                </a>
              </p>

              {/* Paragraph 4 - Upgrade Path */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t(
                  'DeveloperWelcome_Paragraph6',
                  'When you are ready to upgrade to a fully enabled ECGrid account, you may select Manage My Program located next to your document count meter or reach out to your account representative directly.'
                )}
              </p>

              {/* Paragraph 5 - Support Contact */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t('DeveloperWelcome_Paragraph7', 'Our knowledgeable support team can be reached by emailing')}{' '}
                <a
                  href="mailto:support@ecgrid.com"
                  className="font-medium hover:underline"
                  style={{ color: ECGridColors.blue }}
                >
                  support@ecgrid.com
                </a>
                .
              </p>

              {/* Portal Link Section - Only shown when subdomain is available */}
              {subdomain && (
                <div className="my-8 p-6 rounded-lg border-2 text-center"
                     style={{
                       borderColor: ECGridColors.blue,
                       backgroundColor: `${ECGridColors.blue}08`
                     }}>
                  <p className="text-base sm:text-lg font-semibold mb-4"
                     style={{ color: ECGridColors.tealDark }}>
                    {t('DeveloperWelcome_PortalLink_Title', 'Your Portal is Ready')}
                  </p>
                  <a
                    href={`https://${subdomain}.ecgrid.com`}
                    className="inline-block px-8 py-3 rounded-lg text-white font-semibold text-base sm:text-lg transition-all duration-200 hover:opacity-90 hover:shadow-lg"
                    style={{ backgroundColor: ECGridColors.blue }}
                  >
                    {t('DeveloperWelcome_PortalLink_Button', 'Go to Your Portal')}
                  </a>
                  <p className="text-xs sm:text-sm mt-3 text-gray-600">
                    {subdomain}.ecgrid.com
                  </p>
                </div>
              )}

              {/* Paragraph 6 - Closing */}
              <p className="text-base sm:text-lg font-medium text-center mt-8" style={{ color: ECGridColors.tealDark }}>
                {t('DeveloperWelcome_Paragraph8', 'We look forward to supporting your platform as you build and operate your B2B connectivity backbone.')}
              </p>
            </div>
          </div>

          {/* Footer - Outside card */}
      </div>
    </AnonymousPortalLayout>
  );
});

DeveloperWelcomePage.displayName = 'DeveloperWelcomePage';
